import React, { useEffect, useRef, useState } from "react";
import { useThemeContext } from "../../../Context/ThemeContext";
import { Box, Button, Tab, Tabs, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import "./SalesAnalytics.css";
import SalesLineChart from "./SalesLineChart";
import PaymentMethodBarChart from "./PaymentMethodBarChart";
import SalesByLocation from "./SalesByLocation";
import DiscountsCouponsBarChart from "./DiscountsCouponsChartData";
import Card1 from "../../../assets/Cards/Card1.webp";
import Card2 from "../../../assets/Cards/Card2.webp";
import Card3 from "../../../assets/Cards/Card3.webp";
import Card4 from "../../../assets/Cards/Card4.webp";
import CardGroup from "../../../Components/Common/CardGroup";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import { DropDownIcon } from "../../../Components/Layouts/CustomIcons";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LocationPopup from "../LocationPopup";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "../../../Styles/daterange-responsive.css";
import { DateRange } from "react-date-range";
import ProductsChart from "./ProductsChart";
import {
  getSalesCardsCount,
  getReturnsDashboard,
  getUserDetails,
  getFranchiseMachineType,
} from "../../../Services/authService";
import ReturnsByLocation from "./ReturnsByLocation";
import useAuth from "../../../Hooks/useAuth";
import dayjs from "dayjs";
import CashbackMethodsChart from "./CashbackMethodsChart";
import CashbackOverviewChart from "./CashBackOverviewChart";

function SalesAnalytics() {
  const { user } = useAuth();
  const { mode } = useThemeContext();
  const { t } = useTranslation();
  const [franchiseId, setFranchiseId] = useState(null);
  const [machineType, setMachineType] = useState("NONE");

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const data = await getUserDetails();
        setFranchiseId(data?.franchiseId ?? null);
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const fetchMachineType = async () => {
      try {
        const response = await getFranchiseMachineType();

        const type = response?.data?.machineType ?? "NONE";

        setMachineType(type);
      } catch (error) {
        console.error("Error fetching franchise machine type:", error);
        setMachineType("NONE");
      }
    };

    fetchMachineType();
  }, []);

  const calendarRef = useRef(null);
  const calendarButtonRef = useRef(null);

  const containerBackground =
    mode === "dark" ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const hrCircle =
    mode === "dark" ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale30;
  const dropCircle =
    mode === "dark" ? DARKCOLORS.GreyScale50 : LIGHTCOLORS.GreyScale50;
  const dropIcon =
    mode === "dark" ? DARKCOLORS.GreyScale80 : LIGHTCOLORS.GreyScale80;
  const settingIconColor =
    mode === "dark"
      ? DARKCOLORS.HeadingLightBackground
      : LIGHTCOLORS.HeadingLightBackground;
  const btnColor1 =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [machineNumbers, setMachineNumbers] = useState([]);
  const [dateRange, setDateRange] = useState([{ key: "selection" }]);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [applyClicked, setApplyClicked] = useState(false);
  const [returnMetricsData, setReturnMetricsData] = useState([]);
  const [cardData, setCardData] = useState([]);

  // Sales / Returns tab
  const [activeTab, setActiveTab] = useState(0);
  // const showReturnsTab = franchiseId === 1 && !user?.roles?.includes("ROLE_EMPLOYEE");

  // const showSalesTab = machineType === "BOTH" || machineType === "VENDING_ONLY";

  // const showReturnsTab =
  //   machineType === "BOTH" || machineType === "RETURN_ONLY";


const isReturnFoodVending = machineType === "RETURN_AND_FOOD_VENDING";

const showSalesTab =
  machineType === "BOTH" ||
  machineType === "VENDING_ONLY" ||
  isReturnFoodVending;

const showReturnsTab =
  machineType === "BOTH" ||
  machineType === "RETURN_ONLY" ||
  isReturnFoodVending;

  // No vending machines and no return machines assigned to this franchise
  const showNoData = machineType === "NONE";

  useEffect(() => {
  if (machineType === "RETURN_ONLY") {
    setActiveTab(1);
  } else if (
    machineType === "BOTH" ||
    machineType === "VENDING_ONLY" ||
    machineType === "RETURN_AND_FOOD_VENDING"
  ) {
    setActiveTab(0);
  }
}, [machineType]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setIsCalendarOpen(false);
  };

  // // If the user is not allowed to see Returns, always fall back to the Sales tab
  // useEffect(() => {
  //   if (!showReturnsTab && activeTab === 1) {
  //     setActiveTab(0);
  //   }
  // }, [showReturnsTab, activeTab]);

  useEffect(() => {
    if (!showSalesTab && showReturnsTab) {
      setActiveTab(1);
    } else if (showSalesTab && !showReturnsTab) {
      setActiveTab(0);
    }
  }, [showSalesTab, showReturnsTab]);

  const fetchCardsCount = async () => {
    const cardsCount = await getSalesCardsCount();
    const updatedCardData = [
      {
        value: `€ ${cardsCount.totalDaySales}`,
        label: t("sales_analytics.today_sales"),
        background: Card1,
      },
      {
        value: `€ ${cardsCount.totalWeekSales}`,
        label: t("sales_analytics.this_week_sales"),
        background: Card2,
      },
      {
        value: `€ ${cardsCount.totalMonthSales}`,
        label: t("sales_analytics.this_month_sales"),
        background: Card3,
      },
      {
        value: `€ ${cardsCount.totalYearSales}`,
        label: t("sales_analytics.this_year_sales"),
        background: Card4,
      },
    ];
    setCardData(updatedCardData);
  };

  const handleLocationClick = () => setIsPopupOpen(true);
  const handleLocationPopupClose = () => setIsPopupOpen(false);
  const handleDateChange = (item) => {
    setDateRange([item.selection]);
  };

  const handleCalendarToggle = () => {
    if (calendarButtonRef.current) {
      const rect = calendarButtonRef.current.getBoundingClientRect();
      setCalendarPosition({
        top: rect.bottom + window.scrollY + 10,
        left: rect.left + window.scrollX,
      });
    }
    setIsCalendarOpen((prev) => !prev);
  };

  const handleApply = () => {
    setApplyClicked(!applyClicked);
    setIsCalendarOpen(false);
  };

  const handleReset = () => {
    setDateRange([{ key: "selection" }]);
    setApplyClicked((prev) => !prev);
    setIsCalendarOpen(false);
  };

  // Close the calendar when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        calendarButtonRef.current &&
        !calendarButtonRef.current.contains(event.target)
      ) {
        setIsCalendarOpen(false);
      }
    }

    if (isCalendarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCalendarOpen]);

  const handleLocationSubmit = (locations, isAllSelected) => {
    const selectedMachineIds = [];
    locations.forEach((location) => {
      location.machines.forEach((machine) => {
        selectedMachineIds.push(machine.machineId);
      });
    });
    setMachineNumbers(selectedMachineIds); // Store machine numbers in the state
  };

  const fetchReturnMetrics = async () => {
    try {
      const data = await getReturnsDashboard({});

      const metricsData = [
        {
          value: data.totalBowlsReturnedToday,
          label: t("return_metrics.bowlsReturnedToday"),
          background: Card1,
        },
        {
          value: data.totalBottlesReturnedToday,
          label: t("return_metrics.bottlesReturnedToday"),
          background: Card2,
        },
        {
          value: `€ ${data.totalCashbackToday}`,
          label: t("return_metrics.cashBackPaidToday"),
          background: Card3,
        },
        {
          value: data.totalPendingBowls,
          label: t("return_metrics.pendingReturns"),
          background: Card4,
        },
      ];

      setReturnMetricsData(metricsData);
    } catch (error) {
      console.error("Error fetching return metrics:", error);
    }
  };

  useEffect(() => {
    fetchCardsCount();
    fetchReturnMetrics();
  }, [t]);

  // Location + date range filter bar, shared by both tabs
  const renderFilterBar = (isReturns = false) => (
    <Box
      id="view-calender-sec"
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "center",
        justifyContent: "space-between",
        mb: 2,
      }}
    >
      {/* Location Selector acting as a button */}
      <Button
        id="marginZero"
        className="add-machine sales-location-payment"
        onClick={handleLocationClick}
        sx={{
          borderRadius: "24px",
          textAlign: "center",
          padding: "9px 20px",
          display: "flex",
          alignItems: "center",
          color: settingIconColor,
          background: containerBackground,
          textTransform: "none",
          justifyContent: "space-between",
          gap: "8px",
          minWidth: "160px",
        }}
        endIcon={<DropDownIcon color1={dropCircle} color2={dropIcon} />}
      >
        {selectedLocation ? selectedLocation : t("transactions.selectLocation")}
      </Button>

      {/* Location Popup */}
      <LocationPopup
        open={isPopupOpen}
        onClose={handleLocationPopupClose}
        selectedMachineLocations={selectedLocation}
        onSubmit={handleLocationSubmit}
        machineType={isReturns ? "Return Vending Machine" : undefined}
        excludeMachineType={isReturns ? undefined : "Return Vending Machine"}
      />

      {/* Calendar Icon */}
      <Button
        ref={calendarButtonRef}
        aria-label="select-date"
        onClick={handleCalendarToggle}
        sx={{
          background: containerBackground,
          borderRadius: "50px",
          padding: "8px 14px",
          color: dropIcon,
          textTransform: "none",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Typography
          sx={{ fontSize: "13px", color: settingIconColor, fontWeight: 500 }}
        >
          {dateRange[0]?.startDate && dateRange[0]?.endDate
            ? `${dayjs(dateRange[0].startDate).format("DD MMM YYYY")} - ${dayjs(dateRange[0].endDate).format("DD MMM YYYY")}`
            : t("sales_analytics.select_date_range", "Select Date Range")}
        </Typography>
        <CalendarTodayOutlinedIcon style={{ fontSize: "1rem" }} />
      </Button>
      {/* Date Range Picker */}
      {isCalendarOpen && (
        <div
          className="sales-calender"
          ref={calendarRef}
          style={{
            background: containerBackground,
            position: "absolute",
            top: `${calendarPosition.top}px`,
            right: "10px",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            borderRadius: "12px",
          }}
        >
          <DateRange
            editableDateInputs={true}
            onChange={handleDateChange}
            moveRangeOnFirstSelection={false}
            ranges={dateRange}
            style={{
              borderRadius: "12px",
              padding: "8px",
            }}
          />
          <div
            style={{
              textAlign: "right",
              margin: "1rem 0.5rem",
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <button
              className="cancel-btn"
              type="button"
              style={{
                color: btnColor1,
                borderColor: btnColor1,
                padding: "10px 15px",
                fontSize: "15px",
              }}
              onClick={handleReset}
            >
              {t("sales_analytics.reset", "Reset")}
            </button>
            <button
              type="button"
              style={{
                background: btnColor1,
                padding: "10px 15px",
                fontSize: "15px",
              }}
              className="save-btn"
              onClick={handleApply}
            >
              {t("sales_analytics.apply")}
            </button>
          </div>
        </div>
      )}
    </Box>
  );

  // ---------------- NO MACHINE DATA STATE ----------------
  if (showNoData) {
    return (
      <div
        className={`dashboard-container-overview ${mode === "dark" ? "dark-mode" : ""}`}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            width: "100%",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              color: settingIconColor,
            }}
          >
            {t(
              "sales_analytics.no_machine_data",
              "No Machine Data available to display"
            )}
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div
      className={`dashboard-container-overview ${mode === "dark" ? "dark-mode" : ""}`}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        {/* <h2 className='membership-text-heading'>{t('sales_analytics.title')}</h2> */}
      </Box>

      {/* Sales / Returns tabs */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box
          className="res-tab-container"
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "54px",
          }}
        >
          {/* Sales Tab */}
          {showSalesTab && (
            <Box
              onClick={() => setActiveTab(0)}
              sx={{
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minHeight: "42px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "24px",
                }}
              >
                <div
                  className="page-heading"
                  style={{
                    color: activeTab === 0 ? btnColor1 : "#c6c6c6",
                    fontSize: "24px",
                  }}
                >
                  {t("sales_analytics.sales_tab", "Sales")}
                </div>

                {/* Empty space for alignment */}
                <Box
                  sx={{
                    width: "18px",
                  }}
                />
              </Box>

              {/* Active dot */}
              <Box
                sx={{
                  width: "5px",
                  height: "5px",
                  borderRadius: "50%",
                  background: activeTab === 0 ? btnColor1 : "transparent",
                  mt: "26px",
                  alignSelf: "center",
                }}
              />
            </Box>
          )}

          {/* Returns Tab */}
          {showReturnsTab && (
            <Box
              onClick={() => setActiveTab(1)}
              sx={{
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minHeight: "42px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "24px",
                }}
              >
                <div
                  className="page-heading"
                  style={{
                    color: activeTab === 1 ? btnColor1 : "#c6c6c6",
                    fontSize: "24px",
                  }}
                >
                  {t("sales_analytics.returns_tab", "Returns")}
                </div>

                {/* Empty space for alignment */}
                <Box
                  sx={{
                    width: "18px",
                  }}
                />
              </Box>

              {/* Active dot */}
              <Box
                sx={{
                  width: "5px",
                  height: "5px",
                  borderRadius: "50%",
                  background: activeTab === 1 ? btnColor1 : "transparent",
                  mt: "26px",
                  alignSelf: "center",
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* ---------------- SALES TAB ---------------- */}
      {activeTab === 0 && showSalesTab && (
        <>
          <CardGroup cardData={cardData} />

          <div
            className="charts-data-cards"
            style={{ background: containerBackground }}
          >
            <SalesLineChart />
          </div>

          <div
            className="charts-data-cards"
            style={{ background: containerBackground }}
          >
            <SalesByLocation />
          </div>

          <hr style={{ color: hrCircle, margin: "2rem 0rem 1rem" }} />

          {renderFilterBar(false)}

          <div className="twocharts-cards">
            <div
              className="twocharts-data-cards"
              style={{ background: containerBackground }}
            >
              <PaymentMethodBarChart
                selectedLocations={machineNumbers}
                dateRange={dateRange}
                applyClicked={applyClicked}
              />
            </div>
            <div
              className="twocharts-data-cards"
              style={{ background: containerBackground }}
            >
              <DiscountsCouponsBarChart
                selectedLocations={machineNumbers}
                dateRange={dateRange}
                applyClicked={applyClicked}
              />
            </div>
          </div>

          <div className="twocharts-cards">
            <div
              className="twocharts-data-cards"
              style={{ background: containerBackground }}
            >
              <ProductsChart
                selectedLocations={machineNumbers}
                dateRange={dateRange}
                applyClicked={applyClicked}
              />
            </div>
            <div></div>
          </div>
        </>
      )}

      {/* ---------------- RETURNS TAB ---------------- */}
      {activeTab === 1 && showReturnsTab && (
        <>
          <CardGroup cardData={returnMetricsData} />

          <div
            className="charts-data-cards"
            style={{ background: containerBackground }}
          >
            <ReturnsByLocation />
          </div>

          <hr style={{ color: hrCircle, margin: "2rem 0rem 1rem" }} />

          {renderFilterBar(true)}

          <div className="twocharts-cards">
            <div
              className="twocharts-data-cards"
              style={{ background: containerBackground }}
            >
              <CashbackMethodsChart
                selectedLocations={machineNumbers}
                dateRange={dateRange}
                applyClicked={applyClicked}
              />
            </div>
            <div
              className="twocharts-data-cards"
              style={{ background: containerBackground }}
            >
              <CashbackOverviewChart
                selectedLocations={machineNumbers}
                dateRange={dateRange}
                applyClicked={applyClicked}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SalesAnalytics;